import React from "react";
import { Box, Typography, Paper, useTheme, Link } from "@mui/material";
import ReactMarkdown from "react-markdown";

export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatMessageProps {
  message: ChatMessage;
}

const ChatMessageComponent: React.FC<ChatMessageProps> = ({ message }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: message.isUser ? "flex-end" : "flex-start",
        mb: 1,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 1,
          maxWidth: "85%",
          backgroundColor: message.isUser
            ? theme.palette.primary.main
            : isDarkMode
              ? "grey.200"
              : theme.palette.background.default,
          color: message.isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
          borderRadius: message.isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
        }}
      >
        <Box sx={{ wordWrap: "break-word" }}>
          <ReactMarkdown
            components={{
              a: ({ children, href }) => (
                <Link href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Link>
              ),
              p: ({ children }) => (
                <Typography variant="body1" sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
                  {children}
                </Typography>
              ),
              h1: ({ children }) => (
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {children}
                </Typography>
              ),
              h2: ({ children }) => (
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                  {children}
                </Typography>
              ),
              h3: ({ children }) => (
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  {children}
                </Typography>
              ),
              strong: ({ children }) => (
                <Typography component="span" sx={{ fontWeight: "bold" }}>
                  {children}
                </Typography>
              ),
              em: ({ children }) => (
                <Typography component="span" sx={{ fontStyle: "italic" }}>
                  {children}
                </Typography>
              ),
              code: ({ children }) => (
                <Typography
                  component="code"
                  sx={{
                    backgroundColor: message.isUser
                      ? "rgba(255, 255, 255, 0.1)"
                      : isDarkMode
                        ? theme.palette.grey[100]
                        : theme.palette.grey[200],
                    color: message.isUser
                      ? "inherit"
                      : isDarkMode
                        ? theme.palette.text.primary
                        : theme.palette.grey[800],
                    padding: "2px 4px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  }}
                >
                  {children}
                </Typography>
              ),
              pre: ({ children }) => (
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: message.isUser
                      ? "rgba(255, 255, 255, 0.1)"
                      : isDarkMode
                        ? theme.palette.grey[700]
                        : theme.palette.grey[100],
                    color: message.isUser
                      ? "inherit"
                      : isDarkMode
                        ? theme.palette.action[100]
                        : theme.palette.grey[800],
                    padding: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    margin: "8px 0",
                  }}
                >
                  {children}
                </Box>
              ),
              ul: ({ children }) => (
                <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                  {children}
                </Box>
              ),
              ol: ({ children }) => (
                <Box component="ol" sx={{ pl: 2, mb: 1 }}>
                  {children}
                </Box>
              ),
              li: ({ children }) => (
                <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                  {children}
                </Typography>
              ),
              blockquote: ({ children }) => (
                <Box
                  sx={{
                    borderLeft: `4px solid ${theme.palette.grey[400]}`,
                    pl: 2,
                    ml: 1,
                    fontStyle: "italic",
                    opacity: 0.8,
                  }}
                >
                  {children}
                </Box>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        </Box>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.7,
            fontSize: "0.7rem",
            mt: 1,
            display: "block",
          }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      </Paper>
    </Box>
  );
};

export default ChatMessageComponent;
